import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { type ComponentProps, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../dropdown-menu/dropdown-menu';
import { isTableActionAvailable, TABLE_ACTIONS, TABLE_ACTIONS_BY_ID } from './table-actions';

interface TableCellMenuProps {
  container?: ComponentProps<typeof DropdownMenuContent>['container'];
}

/**
 * Per-cell action affordance pinned to the top-right of the active table cell.
 * Mounted as an editor child alongside the toolbar.
 */
export const TableCellMenu = ({ container }: TableCellMenuProps) => {
  const { editor } = useCurrentEditor();
  const { t } = useTranslation();

  const items = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? TABLE_ACTIONS.filter((action) => isTableActionAvailable(action, e)).map((action) => ({
            id: action.id,
            group: action.group,
            label: action.resolveLabelKey?.(e) ?? action.labelKey,
            active: action.isActive?.(e) ?? false,
          }))
        : [],
  });

  if (!editor || !editor.isEditable || !items?.length) {
    return null;
  }

  return (
    <div className="tiptap-cell-menu">
      <DropdownMenu>
        <DropdownMenuTrigger
          className="tiptap-cell-menu-button"
          aria-label={t('table-actions')}
          onMouseDown={(event) => event.preventDefault()}
        >
          <ChevronDown />
        </DropdownMenuTrigger>
        <DropdownMenuContent container={container} side="bottom" align="end" className="w-fit">
          {items.map((item, index) => {
            const action = TABLE_ACTIONS_BY_ID.get(item.id);
            if (!action) {
              return null;
            }

            const Icon = action.icon;
            const showSeparator = index > 0 && items[index - 1]?.group !== item.group;

            return (
              <Fragment key={item.id}>
                {showSeparator && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  variant={action.variant}
                  aria-pressed={item.active || undefined}
                  onClick={() => action.run(editor)}
                >
                  <Icon />
                  {t(item.label)}
                </DropdownMenuItem>
              </Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
